/**
 * GridIndex - Optimized Cell Storage and Lookup
 * 
 * For SheetsApp_v2
 * Provides O(1) cell lookups and efficient row/column queries
 */

export interface CellData {
  ref: string;          // Cell reference (e.g., 'A1')
  value: any;           // Cell value
  formula?: string;     // Formula (if cell contains formula)
  type?: 'text' | 'number' | 'formula' | 'boolean' | 'error';
  format?: CellFormat;  // Cell formatting
  error?: string;       // Error message (if formula failed)
}

export interface CellFormat {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  numberFormat?: string; // e.g., '0.00', '#,##0', 'mm/dd/yyyy'
}

/**
 * GridIndex - Sparse cell storage with fast lookups
 */
export class GridIndex {
  // ========================================================================
  // CELL STORAGE
  // ========================================================================
  
  // Primary storage - sparse (only stores non-empty cells)
  private cells: Map<string, CellData> = new Map(); // 'A1' → CellData
  
  // ========================================================================
  // FAST LOOKUP INDICES
  // ========================================================================
  
  // Row index - row number → Set of cell refs in that row
  private rowIndex: Map<number, Set<string>> = new Map();
  
  // Column index - column letter → Set of cell refs in that column
  private colIndex: Map<string, Set<string>> = new Map();
  
  // Formula dependencies - cell ref → Set of cells that depend on it
  private formulaDependencies: Map<string, Set<string>> = new Map();
  
  // Reverse dependencies - formula cell → Set of cells it depends on
  private formulaReferences: Map<string, Set<string>> = new Map();
  
  // ========================================================================
  // CELL OPERATIONS
  // ========================================================================
  
  /**
   * Set cell data - O(1)
   */
  setCell(ref: string, data: CellData): void {
    const normalizedRef = ref.toUpperCase();
    const { row, col } = this.parseCellRef(normalizedRef);
    
    // Store cell data
    this.cells.set(normalizedRef, { ...data, ref: normalizedRef });
    
    // Index by row
    if (!this.rowIndex.has(row)) {
      this.rowIndex.set(row, new Set());
    }
    this.rowIndex.get(row)!.add(normalizedRef);
    
    // Index by column
    if (!this.colIndex.has(col)) {
      this.colIndex.set(col, new Set());
    }
    this.colIndex.get(col)!.add(normalizedRef);
    
    console.log('[GridIndex] Set cell:', { ref: normalizedRef, value: data.value, formula: data.formula });
  }
  
  /**
   * Get cell data - O(1)
   */
  getCell(ref: string): CellData | null {
    return this.cells.get(ref.toUpperCase()) || null;
  }
  
  /**
   * Delete cell - O(1)
   */
  deleteCell(ref: string): void {
    const normalizedRef = ref.toUpperCase();
    const { row, col } = this.parseCellRef(normalizedRef);
    
    // Remove from primary storage
    this.cells.delete(normalizedRef);
    
    // Remove from row index
    this.rowIndex.get(row)?.delete(normalizedRef);
    if (this.rowIndex.get(row)?.size === 0) {
      this.rowIndex.delete(row);
    }
    
    // Remove from column index
    this.colIndex.get(col)?.delete(normalizedRef);
    if (this.colIndex.get(col)?.size === 0) {
      this.colIndex.delete(col);
    }
    
    // Remove from dependency tracking
    this.formulaDependencies.delete(normalizedRef);
    this.formulaReferences.delete(normalizedRef);
  }
  
  // ========================================================================
  // RANGE QUERIES
  // ========================================================================
  
  /**
   * Get entire row - O(1) + O(m) where m = cells in row
   */
  getRow(rowNumber: number): CellData[] {
    const cellRefs = this.rowIndex.get(rowNumber) || new Set();
    
    return Array.from(cellRefs)
      .map(ref => this.cells.get(ref))
      .filter((cell): cell is CellData => cell !== undefined)
      .sort((a, b) => {
        const colA = this.parseCellRef(a.ref).col;
        const colB = this.parseCellRef(b.ref).col;
        return this.colToNumber(colA) - this.colToNumber(colB);
      });
  }
  
  /**
   * Get entire column - O(1) + O(m) where m = cells in column
   */
  getColumn(col: string): CellData[] {
    const normalizedCol = col.toUpperCase();
    const cellRefs = this.colIndex.get(normalizedCol) || new Set();
    
    return Array.from(cellRefs)
      .map(ref => this.cells.get(ref))
      .filter((cell): cell is CellData => cell !== undefined)
      .sort((a, b) => this.parseCellRef(a.ref).row - this.parseCellRef(b.ref).row);
  }
  
  /**
   * Get cell range (e.g., 'A1:C10') - O(cells in range)
   */
  getRange(rangeStr: string): CellData[] {
    const [startRef, endRef] = rangeStr.split(':').map(r => r.trim().toUpperCase());
    const start = this.parseCellRef(startRef);
    const end = this.parseCellRef(endRef);
    
    const results: CellData[] = [];
    
    const startColNum = this.colToNumber(start.col);
    const endColNum = this.colToNumber(end.col);
    const startRow = Math.min(start.row, end.row);
    const endRow = Math.max(start.row, end.row);
    
    for (let row = startRow; row <= endRow; row++) {
      for (let colNum = startColNum; colNum <= endColNum; colNum++) {
        const col = this.numberToCol(colNum);
        const ref = `${col}${row}`;
        const cell = this.cells.get(ref);
        
        if (cell) {
          results.push(cell);
        } else {
          // Return empty cell placeholder
          results.push({
            ref,
            value: null,
            type: 'text'
          });
        }
      }
    }
    
    return results;
  }
  
  /**
   * Get specific cells - O(k) where k = number of cells requested
   */
  getCells(refs: string[]): CellData[] {
    return refs
      .map(ref => this.getCell(ref))
      .filter((cell): cell is CellData => cell !== null);
  }
  
  // ========================================================================
  // FORMULA DEPENDENCIES
  // ========================================================================
  
  /**
   * Track formula dependency
   * When cellA formula references cellB, cellB is a dependency of cellA
   */
  addDependency(formulaCell: string, dependsOnCell: string): void {
    const normFormula = formulaCell.toUpperCase();
    const normDep = dependsOnCell.toUpperCase();
    
    // Track: dependsOnCell → formulaCells that reference it
    if (!this.formulaDependencies.has(normDep)) {
      this.formulaDependencies.set(normDep, new Set());
    }
    this.formulaDependencies.get(normDep)!.add(normFormula);
    
    // Track reverse: formulaCell → cells it references
    if (!this.formulaReferences.has(normFormula)) {
      this.formulaReferences.set(normFormula, new Set());
    }
    this.formulaReferences.get(normFormula)!.add(normDep);
  }
  
  /**
   * Get cells that depend on this cell (for recalculation)
   */
  getDependents(cellRef: string): string[] {
    return Array.from(this.formulaDependencies.get(cellRef.toUpperCase()) || []);
  }
  
  /**
   * Get cells this formula references
   */
  getReferences(formulaCell: string): string[] {
    return Array.from(this.formulaReferences.get(formulaCell.toUpperCase()) || []);
  }
  
  /**
   * Detect circular references
   */
  hasCircularReference(cellRef: string, visited = new Set<string>()): boolean {
    const normalized = cellRef.toUpperCase();
    
    if (visited.has(normalized)) {
      return true; // Circular reference detected!
    }
    
    visited.add(normalized);
    
    const dependents = this.getDependents(normalized);
    for (const dependent of dependents) {
      if (this.hasCircularReference(dependent, new Set(visited))) {
        return true;
      }
    }
    
    return false;
  }
  
  // ========================================================================
  // UTILITY METHODS
  // ========================================================================
  
  /**
   * Parse cell reference 'A1' → { col: 'A', row: 1 }
   */
  parseCellRef(ref: string): { col: string; row: number } {
    const match = ref.match(/^([A-Z]+)(\d+)$/);
    if (!match) {
      throw new Error(`Invalid cell reference: ${ref}`);
    }
    
    return {
      col: match[1],
      row: parseInt(match[2], 10)
    };
  }
  
  /**
   * Convert column letter to number (A=0, B=1, Z=25, AA=26, ...)
   */
  colToNumber(col: string): number {
    let num = 0;
    for (let i = 0; i < col.length; i++) {
      num = num * 26 + (col.charCodeAt(i) - 64);
    }
    return num - 1;
  }
  
  /**
   * Convert number to column letter (0=A, 1=B, 25=Z, 26=AA, ...)
   */
  numberToCol(num: number): string {
    let col = '';
    let n = num + 1;
    
    while (n > 0) {
      const remainder = (n - 1) % 26;
      col = String.fromCharCode(65 + remainder) + col;
      n = Math.floor((n - 1) / 26);
    }
    
    return col;
  }
  
  /**
   * Format A1 notation from row/col numbers
   */
  formatCellRef(row: number, col: number): string {
    return `${this.numberToCol(col)}${row}`;
  }
  
  /**
   * Clear all data
   */
  clear(): void {
    this.cells.clear();
    this.rowIndex.clear();
    this.colIndex.clear();
    this.formulaDependencies.clear();
    this.formulaReferences.clear();
  }
  
  /**
   * Get statistics
   */
  getStats(): {
    totalCells: number;
    formulaCells: number;
    rows: number;
    cols: number;
  } {
    const formulaCells = Array.from(this.cells.values()).filter(c => c.type === 'formula').length;
    
    return {
      totalCells: this.cells.size,
      formulaCells,
      rows: this.rowIndex.size,
      cols: this.colIndex.size
    };
  }
  
  /**
   * Check if cell exists
   */
  hasCell(ref: string): boolean {
    return this.cells.has(ref.toUpperCase());
  }
  
  /**
   * Get all cell references
   */
  getAllCellRefs(): string[] {
    return Array.from(this.cells.keys());
  }
}
